<template>
	<div class="animated fadeIn">

		<b-row class="mb-3 align-items-center">
			<b-col>
				<h4 class="mb-0"><i class="fa fa-users mr-2 text-primary"></i>Clients</h4>
				<small class="text-muted">Every office and its Tipsoi Pay billing state</small>
			</b-col>
			<b-col class="text-right">
				<b-button variant="outline-secondary" size="sm" class="mr-1" @click="exportCsv" :disabled="loading || !clients.length">
					<i class="fa fa-download mr-1"></i>CSV
				</b-button>
				<b-button variant="outline-secondary" size="sm" @click="load" :disabled="loading">
					<i class="icon-reload mr-1"></i>Refresh
				</b-button>
			</b-col>
		</b-row>

		<!-- Toolbar -->
		<b-row class="mb-3 align-items-center">
			<b-col md="5" class="mb-2 mb-md-0">
				<b-input-group size="sm">
					<b-input-group-prepend is-text><i class="fa fa-search"></i></b-input-group-prepend>
					<b-form-input v-model="search" type="search" placeholder="Office, client panel, or Tipsoi Pay id…"/>
				</b-input-group>
			</b-col>
			<b-col md="7" class="text-md-right">
				<b-form-radio-group v-model="filter" size="sm" buttons button-variant="outline-secondary">
					<b-form-radio value="ALL">All</b-form-radio>
					<b-form-radio value="OVERDUE">Overdue</b-form-radio>
					<b-form-radio value="DUE_SOON">Due soon</b-form-radio>
					<b-form-radio value="CURRENT">Current</b-form-radio>
					<b-form-radio value="LINKED">Linked</b-form-radio>
					<b-form-radio value="NOT_LINKED">Not linked</b-form-radio>
					<b-form-radio value="ENFORCING">Enforcing</b-form-radio>
				</b-form-radio-group>
			</b-col>
		</b-row>

		<div v-if="loading" class="text-center py-5">
			<b-spinner variant="info" style="width:3rem;height:3rem;"></b-spinner>
		</div>

		<div v-else>
			<b-alert v-if="loadError" show variant="warning" class="small">{{ loadError }}</b-alert>

			<template v-else>
				<b-card no-body>
					<div class="table-responsive">
						<table class="table table-sm table-striped table-hover mb-0">
							<thead>
								<tr>
									<th>Office</th>
									<th>Client panel</th>
									<th>Pay client</th>
									<th>Pay project</th>
									<th>State</th>
									<th>Due date</th>
									<th class="text-right">Last paid</th>
									<th>Last payment</th>
									<th class="text-center">Enforcing</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="c in clients" :key="c.officeId">
									<td>
										{{ c.officeName }}
										<div class="text-muted tiny">#{{ c.officeId }}</div>
									</td>
									<td class="small">{{ c.clientPanelTitle || '—' }}</td>
									<td><code v-if="c.billingClientId">{{ c.billingClientId }}</code><span v-else class="text-muted">—</span></td>
									<td><code v-if="c.billingProjectId">{{ c.billingProjectId }}</code><span v-else class="text-muted">—</span></td>
									<td>
										<span class="state" :class="'state-' + c.state">
											<span class="dot"></span>{{ stateLabel(c) }}
										</span>
									</td>
									<td class="small text-muted">{{ formatDate(c.billingDueDate) }}</td>
									<td class="text-right num">{{ formatMoney(c.billingCashPaidAmount) }}</td>
									<td class="small text-muted">{{ formatDate(c.billingLastPaymentDate) }}</td>
									<td class="text-center">
										<i v-if="c.enforcing" class="fa fa-check text-info" title="Auto-deactivate on non-payment is on"></i>
										<span v-else class="text-muted">—</span>
									</td>
									<td class="text-right">
										<a v-if="c.billingInvoiceUrl" :href="c.billingInvoiceUrl" target="_blank"
											 rel="noopener" class="small" title="Open this client in Tipsoi Pay">Open ↗</a>
									</td>
								</tr>
								<tr v-if="!clients.length">
									<td colspan="10" class="text-center text-muted py-4">No client matches this filter.</td>
								</tr>
							</tbody>
						</table>
					</div>
				</b-card>

				<b-row class="mt-3 align-items-center">
					<b-col md="6">
						<small class="text-muted">
							{{ total }} client<span v-if="total !== 1">s</span>
							<span v-if="filter !== 'ALL'"> matching this filter</span>
						</small>
					</b-col>
					<b-col md="6" class="text-md-right">
						<b-pagination
							v-if="numberOfPages > 1"
							v-model="uiPage"
							:total-rows="total"
							:per-page="perPage"
							size="sm"
							align="right"
							class="mb-0">
						</b-pagination>
					</b-col>
				</b-row>

				<p class="text-muted tiny mt-3 mb-0">
					<i class="fa fa-info-circle mr-1"></i>
					Enforcing is shown read-only. The flag is cached by the HRM API, so changing it here
					would not take effect until that cache refreshed — it stays editable where it already
					works rather than being duplicated as a switch that appears to do nothing.
				</p>
			</template>
		</div>
	</div>
</template>

<script>
import moment from 'moment';
import payService from '@/services/payService';

const STATE_LABELS = {
	OVERDUE: 'Overdue',
	DUE_SOON: 'Due soon',
	CURRENT: 'Current',
	NO_DUE_DATE: 'No due date',
	NOT_LINKED: 'Not linked'
};

export default {
	name: 'PayClients',

	data() {
		return {
			loading: false,
			loadError: '',
			search: '',
			filter: 'ALL',
			pageNumber: 0,
			perPage: 25,
			clients: [],
			total: 0,
			numberOfPages: 0,
			searchTimer: null
		};
	},

	computed: {
		// b-pagination is 1-based; the API is 0-based.
		uiPage: {
			get() { return this.pageNumber + 1; },
			set(value) { this.pageNumber = Math.max(0, value - 1); this.load(); }
		}
	},

	watch: {
		filter() {
			this.pageNumber = 0;
			this.load();
		},
		search() {
			// Debounced so typing does not fire a request per keystroke.
			clearTimeout(this.searchTimer);
			this.searchTimer = setTimeout(() => {
				this.pageNumber = 0;
				this.load();
			}, 350);
		}
	},

	methods: {
		load() {
			this.loading = true;
			this.loadError = '';

			payService.getClients({
				filter: this.filter,
				search: this.search || undefined,
				pageNumber: this.pageNumber,
				perPage: this.perPage
			}, (data) => {
				this.loading = false;
				if (!data || data.available === false) {
					this.loadError = (data && data.error) || 'The Tipsoi Pay client list is not available.';
					return;
				}
				this.clients = data.clients || [];
				this.total = data.total || 0;
				this.numberOfPages = data.numberOfPages || 0;
			}, (err) => {
				this.loading = false;
				this.loadError = err;
			});
		},

		stateLabel(client) {
			if (client.state === 'OVERDUE' && client.daysToDue !== null && client.daysToDue !== undefined) {
				return 'Overdue ' + (-client.daysToDue) + ' d';
			}
			if (client.state === 'DUE_SOON' && client.daysToDue !== null && client.daysToDue !== undefined) {
				return 'Due in ' + client.daysToDue + ' d';
			}
			return STATE_LABELS[client.state] || client.state;
		},

		formatDate(millis) {
			if (!millis) return '—';
			return moment(millis).format('DD MMM YYYY');
		},

		formatMoney(amount) {
			if (amount === null || amount === undefined) return '—';
			return Number(amount).toLocaleString('en-IN');
		},

		exportCsv() {
			const header = ['Office ID', 'Office', 'Client panel', 'Pay client id', 'Pay project id',
				'State', 'Days to due', 'Due date', 'Last paid', 'Last payment', 'Enforcing', 'Invoice URL'];

			const rows = this.clients.map(c => [
				c.officeId,
				c.officeName,
				c.clientPanelTitle,
				c.billingClientId,
				c.billingProjectId,
				c.state,
				c.daysToDue,
				c.billingDueDate ? moment(c.billingDueDate).format('YYYY-MM-DD') : '',
				c.billingCashPaidAmount,
				c.billingLastPaymentDate ? moment(c.billingLastPaymentDate).format('YYYY-MM-DD') : '',
				c.enforcing ? 'yes' : 'no',
				c.billingInvoiceUrl
			]);

			const csv = [header].concat(rows)
				.map(row => row.map(cell => csvCell(cell)).join(','))
				.join('\r\n');

			// The BOM makes Excel read this as UTF-8 rather than mangling non-ASCII office names.
			const blob = new Blob(['﻿' + csv], {type: 'text/csv;charset=utf-8;'});
			const link = document.createElement('a');
			link.href = URL.createObjectURL(blob);
			link.setAttribute('download', 'tipsoi-pay-clients-' + moment().format('YYYYMMDD') + '.csv');
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(link.href);
		}
	},

	created() {
		if (this.$route.query.filter) this.filter = this.$route.query.filter;
		this.load();
	}
};

function csvCell(value) {
	if (value === null || value === undefined) return '';
	const text = String(value);
	return /[",\r\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
}
</script>

<style scoped>
.state {
	display: inline-flex;
	align-items: center;
	font-size: 0.8rem;
	white-space: nowrap;
}

.state .dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	margin-right: 0.4rem;
	background: #c8ced3;
}

.state-OVERDUE { color: #c8433f; }
.state-OVERDUE .dot { background: #f86c6b; }
.state-DUE_SOON { color: #8a6d0b; }
.state-DUE_SOON .dot { background: #ffc107; }
.state-CURRENT { color: #2f8f52; }
.state-CURRENT .dot { background: #4dbd74; }
.state-NO_DUE_DATE { color: #73818f; }
.state-NOT_LINKED { color: #9aa5ad; }

.num { font-variant-numeric: tabular-nums; }

.tiny { font-size: 0.72rem; }
</style>
